/**
 * Branded Layer Strategy Implementation
 *
 * Implements layer-specific logic for Branded (B) layer processing.
 * Branded layer handles brand integration, product placement, and commercial content.
 * This layer can include images or videos with brand-specific assets.
 */

import {
  BaseLayerStrategy,
  LayerFieldConfig,
  ValidationResult,
} from './LayerStrategy';

export interface BrandedLayerMetadata {
  layerType: 'branded';

  // Core brand information
  brandName?: string;
  brandType?:
    | 'Fashion'
    | 'Tech'
    | 'Food_Beverage'
    | 'Automotive'
    | 'Cosmetics'
    | 'Sports'
    | 'Luxury'
    | 'Entertainment'
    | 'Crypto_Web3';
  description?: string;

  // Brand identity
  brandLogo?: string; // URL to brand logo
  brandColors?: string[]; // brand color palette
  brandSlogan?: string;
  brandCategory?: string; // industry category

  // Product information
  productName?: string;
  productType?: string;
  productDescription?: string;
  productPrice?: number;
  currency?: string;

  // Campaign and marketing
  campaignName?: string;
  campaignType?:
    | 'Product_Launch'
    | 'Brand_Awareness'
    | 'Influencer_Partnership'
    | 'Seasonal'
    | 'Event_Promotion';
  targetAudience?: string[];
  marketingObjectives?: string[];

  // Brand assets and content
  contentType?: 'Image' | 'Video' | 'Mixed_Media';
  assetStyle?: string; // visual style of the branded content
  brandPlacement?: 'Prominent' | 'Subtle' | 'Integrated' | 'Background';

  // Commercial details
  sponsorshipLevel?: 'Title' | 'Major' | 'Supporting' | 'Mention';
  contractTerms?: string[];
  usageRights?: string[];
  exclusivity?: boolean;

  // Performance metrics
  impressions?: number;
  engagement?: number;
  clickThrough?: number;
  conversionRate?: number;

  // AI analysis
  brandRecognition?: number; // 0-1 how recognizable the brand is
  brandAlignment?: number; // 0-1 how well brand aligns with content
  commercialAppeal?: number; // 0-1 commercial effectiveness
  authenticity?: number; // 0-1 how authentic the integration feels

  // Compliance and legal
  regulatoryCompliance?: string[]; // advertising standards compliance
  disclosureRequired?: boolean; // requires #ad or #sponsored
  ageRestrictions?: string;
  geographicRestrictions?: string[];

  // Associated assets (brand can be integrated with other layers)
  integratedAssets?: {
    songAssets?: string[]; // G layer assets featuring this brand
    starAssets?: string[]; // S layer assets endorsing this brand
    lookAssets?: string[]; // L layer assets featuring brand products
    moveAssets?: string[]; // M layer assets in branded campaigns
    worldAssets?: string[]; // W layer assets with brand environments
    compositeAssets?: string[]; // C layer assets with brand integration
  };

  // Social media optimization
  hashtags?: string[];
  socialPlatforms?: string[]; // target platforms
  viralPotential?: number; // 0-1 likelihood of viral spread
  trendAlignment?: string[]; // current trends brand aligns with

  // Brand partnerships
  collaboratingBrands?: string[]; // other brands in the same content
  influencerTier?: 'Nano' | 'Micro' | 'Macro' | 'Mega' | 'Celebrity';
  partnershipType?: 'Paid' | 'Gifted' | 'Equity' | 'Long_Term_Contract';

  // Technical metadata
  brandLogoDetection?: boolean; // AI detected brand logo
  productDetection?: string[]; // AI detected products
  brandMentions?: number; // number of brand mentions
  visualBrandElements?: string[]; // detected visual brand elements
}

export class BrandedLayerStrategy extends BaseLayerStrategy {
  readonly layerCode = 'B';
  readonly layerName = 'Branded';
  readonly layerDescription =
    'Brand integration, product placement, and commercial content assets';

  getRequiredFields(): string[] {
    return ['brandName', 'brandType'];
  }

  getOptionalFields(): string[] {
    return [
      'description',
      'brandLogo',
      'brandColors',
      'brandSlogan',
      'brandCategory',
      'productName',
      'productType',
      'productDescription',
      'productPrice',
      'currency',
      'campaignName',
      'campaignType',
      'targetAudience',
      'marketingObjectives',
      'contentType',
      'assetStyle',
      'brandPlacement',
      'sponsorshipLevel',
      'contractTerms',
      'usageRights',
      'exclusivity',
      'impressions',
      'engagement',
      'clickThrough',
      'conversionRate',
      'brandRecognition',
      'brandAlignment',
      'commercialAppeal',
      'authenticity',
      'regulatoryCompliance',
      'disclosureRequired',
      'ageRestrictions',
      'geographicRestrictions',
      'integratedAssets',
      'hashtags',
      'socialPlatforms',
      'viralPotential',
      'trendAlignment',
      'collaboratingBrands',
      'influencerTier',
      'partnershipType',
      'brandLogoDetection',
      'productDetection',
      'brandMentions',
      'visualBrandElements',
    ];
  }

  getFieldConfig(): LayerFieldConfig {
    return {
      required: this.getRequiredFields(),
      optional: this.getOptionalFields(),
      computed: [
        'brandRecognition',
        'brandAlignment',
        'commercialAppeal',
        'authenticity',
        'viralPotential',
        'brandLogoDetection',
        'productDetection',
        'brandMentions',
      ],
      inherited: ['brandColors', 'targetAudience', 'marketingObjectives'],
    };
  }

  extractMetadataFromAI(aiResponse: any): BrandedLayerMetadata {
    const layerMetadata =
      aiResponse.layerMetadata || aiResponse.brandedMetadata || {};

    return {
      layerType: 'branded',

      // Core information
      brandName: layerMetadata.brandName || aiResponse.brandName || '',
      brandType: this.normalizeBrandType(
        layerMetadata.brandType || aiResponse.brandType
      ),
      description: layerMetadata.description || aiResponse.description || '',

      // Brand identity
      brandLogo: layerMetadata.brandLogo || aiResponse.brandLogo,
      brandColors: this.normalizeArray(
        layerMetadata.brandColors || aiResponse.brandColors
      ),
      brandSlogan: layerMetadata.brandSlogan || aiResponse.brandSlogan,
      brandCategory: layerMetadata.brandCategory || aiResponse.brandCategory,

      // Product information
      productName: layerMetadata.productName || aiResponse.productName,
      productType: layerMetadata.productType || aiResponse.productType,
      productDescription:
        layerMetadata.productDescription || aiResponse.productDescription,
      productPrice: layerMetadata.productPrice || aiResponse.productPrice,
      currency: layerMetadata.currency || aiResponse.currency,

      // Campaign details
      campaignName: layerMetadata.campaignName || aiResponse.campaignName,
      campaignType: this.normalizeCampaignType(
        layerMetadata.campaignType || aiResponse.campaignType
      ),
      targetAudience: this.normalizeArray(
        layerMetadata.targetAudience || aiResponse.targetAudience
      ),
      marketingObjectives: this.normalizeArray(
        layerMetadata.marketingObjectives || aiResponse.marketingObjectives
      ),

      // Content characteristics
      contentType: this.normalizeContentType(
        layerMetadata.contentType || aiResponse.contentType
      ),
      assetStyle: layerMetadata.assetStyle || aiResponse.assetStyle,
      brandPlacement: this.normalizeBrandPlacement(
        layerMetadata.brandPlacement || aiResponse.brandPlacement
      ),

      // Commercial details
      sponsorshipLevel: this.normalizeSponsorshipLevel(
        layerMetadata.sponsorshipLevel || aiResponse.sponsorshipLevel
      ),
      contractTerms: this.normalizeArray(
        layerMetadata.contractTerms || aiResponse.contractTerms
      ),
      usageRights: this.normalizeArray(
        layerMetadata.usageRights || aiResponse.usageRights
      ),
      exclusivity: layerMetadata.exclusivity ?? aiResponse.exclusivity,

      // AI analysis
      brandRecognition:
        layerMetadata.brandRecognition || aiResponse.brandRecognition,
      brandAlignment: layerMetadata.brandAlignment || aiResponse.brandAlignment,
      commercialAppeal:
        layerMetadata.commercialAppeal || aiResponse.commercialAppeal,
      authenticity: layerMetadata.authenticity || aiResponse.authenticity,

      // Compliance
      regulatoryCompliance: this.normalizeArray(
        layerMetadata.regulatoryCompliance || aiResponse.regulatoryCompliance
      ),
      disclosureRequired:
        layerMetadata.disclosureRequired ?? aiResponse.disclosureRequired,
      ageRestrictions:
        layerMetadata.ageRestrictions || aiResponse.ageRestrictions,
      geographicRestrictions: this.normalizeArray(
        layerMetadata.geographicRestrictions ||
          aiResponse.geographicRestrictions
      ),

      // Integration
      integratedAssets: this.extractIntegratedAssets(
        layerMetadata.integratedAssets || aiResponse.integratedAssets
      ),

      // Social media
      hashtags: this.normalizeArray(
        layerMetadata.hashtags || aiResponse.hashtags
      ),
      socialPlatforms: this.normalizeArray(
        layerMetadata.socialPlatforms || aiResponse.socialPlatforms
      ),
      viralPotential: layerMetadata.viralPotential || aiResponse.viralPotential,
      trendAlignment: this.normalizeArray(
        layerMetadata.trendAlignment || aiResponse.trendAlignment
      ),

      // Partnerships
      collaboratingBrands: this.normalizeArray(
        layerMetadata.collaboratingBrands || aiResponse.collaboratingBrands
      ),
      influencerTier: this.normalizeInfluencerTier(
        layerMetadata.influencerTier || aiResponse.influencerTier
      ),
      partnershipType: this.normalizePartnershipType(
        layerMetadata.partnershipType || aiResponse.partnershipType
      ),

      // Technical detection
      brandLogoDetection:
        layerMetadata.brandLogoDetection ?? aiResponse.brandLogoDetection,
      productDetection: this.normalizeArray(
        layerMetadata.productDetection || aiResponse.productDetection
      ),
      brandMentions: layerMetadata.brandMentions || aiResponse.brandMentions,
      visualBrandElements: this.normalizeArray(
        layerMetadata.visualBrandElements || aiResponse.visualBrandElements
      ),
    };
  }

  enhanceWithDefaults(metadata: BrandedLayerMetadata): BrandedLayerMetadata {
    return {
      ...this.getDefaultMetadata(),
      ...metadata,
      layerType: 'branded',

      // Ensure required fields have defaults
      brandName: metadata.brandName || 'Unknown Brand',
      brandType: metadata.brandType || 'Fashion',

      // Normalize arrays
      brandColors: this.normalizeArray(metadata.brandColors),
      targetAudience: this.normalizeArray(metadata.targetAudience),
      marketingObjectives: this.normalizeArray(metadata.marketingObjectives),
      contractTerms: this.normalizeArray(metadata.contractTerms),
      usageRights: this.normalizeArray(metadata.usageRights),
      regulatoryCompliance: this.normalizeArray(metadata.regulatoryCompliance),
      geographicRestrictions: this.normalizeArray(
        metadata.geographicRestrictions
      ),
      hashtags: this.normalizeArray(metadata.hashtags),
      socialPlatforms: this.normalizeArray(metadata.socialPlatforms),
      trendAlignment: this.normalizeArray(metadata.trendAlignment),
      collaboratingBrands: this.normalizeArray(metadata.collaboratingBrands),
      productDetection: this.normalizeArray(metadata.productDetection),
      visualBrandElements: this.normalizeArray(metadata.visualBrandElements),

      // Default values
      brandPlacement: metadata.brandPlacement || 'Integrated',
      sponsorshipLevel: metadata.sponsorshipLevel || 'Supporting',
      influencerTier: metadata.influencerTier || 'Micro',
      partnershipType: metadata.partnershipType || 'Paid',
      currency: metadata.currency || 'USD',
      exclusivity: metadata.exclusivity ?? false,
      disclosureRequired: metadata.disclosureRequired ?? true,

      // Ensure object fields exist
      integratedAssets: metadata.integratedAssets || {},
    };
  }

  generateTags(metadata: BrandedLayerMetadata, description?: string): string[] {
    const tags = new Set<string>();

    // Basic layer tags
    tags.add('branded');
    tags.add('commercial');
    tags.add('brand');

    // Brand type tags
    if (metadata.brandType) {
      tags.add(metadata.brandType.toLowerCase().replace(/_/g, '-'));
      switch (metadata.brandType) {
        case 'Fashion':
          tags.add('fashion-brand');
          tags.add('apparel');
          break;
        case 'Tech':
          tags.add('technology');
          tags.add('tech-brand');
          break;
        case 'Food_Beverage':
          tags.add('food');
          tags.add('beverage');
          tags.add('fmcg');
          break;
        case 'Automotive':
          tags.add('cars');
          tags.add('automotive');
          break;
        case 'Cosmetics':
          tags.add('beauty');
          tags.add('cosmetics');
          break;
        case 'Sports':
          tags.add('sports');
          tags.add('athletic');
          break;
        case 'Luxury':
          tags.add('luxury');
          tags.add('premium');
          break;
        case 'Entertainment':
          tags.add('entertainment');
          tags.add('media');
          break;
        case 'Crypto_Web3':
          tags.add('crypto');
          tags.add('web3');
          tags.add('blockchain');
          break;
      }
    }

    // Brand name tags
    if (metadata.brandName) {
      tags.add(metadata.brandName.toLowerCase().replace(/\s+/g, '-'));
      tags.add(`${metadata.brandName.toLowerCase()}-brand`);
    }

    // Campaign type tags
    if (metadata.campaignType) {
      tags.add(metadata.campaignType.toLowerCase().replace(/_/g, '-'));
      switch (metadata.campaignType) {
        case 'Product_Launch':
          tags.add('product-launch');
          tags.add('new-product');
          break;
        case 'Brand_Awareness':
          tags.add('brand-awareness');
          tags.add('marketing');
          break;
        case 'Influencer_Partnership':
          tags.add('influencer');
          tags.add('partnership');
          break;
        case 'Seasonal':
          tags.add('seasonal');
          tags.add('holiday');
          break;
        case 'Event_Promotion':
          tags.add('event');
          tags.add('promotion');
          break;
      }
    }

    // Brand placement tags
    if (metadata.brandPlacement) {
      tags.add(`${metadata.brandPlacement.toLowerCase()}-placement`);
      switch (metadata.brandPlacement) {
        case 'Prominent':
          tags.add('prominent');
          tags.add('featured');
          break;
        case 'Subtle':
          tags.add('subtle');
          tags.add('organic');
          break;
        case 'Integrated':
          tags.add('integrated');
          tags.add('natural');
          break;
        case 'Background':
          tags.add('background');
          tags.add('ambient');
          break;
      }
    }

    // Sponsorship level tags
    if (metadata.sponsorshipLevel) {
      tags.add(`${metadata.sponsorshipLevel.toLowerCase()}-sponsor`);
    }

    // Target audience tags
    if (metadata.targetAudience && metadata.targetAudience.length > 0) {
      metadata.targetAudience.forEach(audience => {
        tags.add(audience.toLowerCase().replace(/\s+/g, '-'));
      });
    }

    // Social platform tags
    if (metadata.socialPlatforms && metadata.socialPlatforms.length > 0) {
      metadata.socialPlatforms.forEach(platform => {
        tags.add(`${platform.toLowerCase()}-optimized`);
      });
    }

    // Content type tags
    if (metadata.contentType) {
      tags.add(metadata.contentType.toLowerCase().replace(/_/g, '-'));
    }

    // Influencer tier tags
    if (metadata.influencerTier) {
      tags.add(`${metadata.influencerTier.toLowerCase()}-influencer`);
    }

    // Partnership type tags
    if (metadata.partnershipType) {
      tags.add(
        `${metadata.partnershipType
          .toLowerCase()
          .replace(/_/g, '-')}-partnership`
      );
    }

    // Commercial appeal tags
    if (metadata.commercialAppeal && metadata.commercialAppeal > 0.7) {
      tags.add('high-commercial-appeal');
      tags.add('marketable');
    }

    // Viral potential tags
    if (metadata.viralPotential && metadata.viralPotential > 0.7) {
      tags.add('viral-potential');
      tags.add('shareable');
    }

    // Compliance tags
    if (metadata.disclosureRequired) {
      tags.add('disclosure-required');
      tags.add('sponsored-content');
    }

    if (metadata.exclusivity) {
      tags.add('exclusive');
      tags.add('exclusive-partnership');
    }

    // Hashtag tags (add relevant ones)
    if (metadata.hashtags && metadata.hashtags.length > 0) {
      metadata.hashtags.slice(0, 5).forEach(hashtag => {
        const cleanTag = hashtag.replace('#', '').toLowerCase();
        if (cleanTag.length > 2 && cleanTag.length < 20) {
          tags.add(cleanTag);
        }
      });
    }

    // AI detection tags
    if (metadata.brandLogoDetection) {
      tags.add('logo-detected');
      tags.add('brand-visible');
    }

    if (metadata.productDetection && metadata.productDetection.length > 0) {
      tags.add('product-placement');
      tags.add('product-visible');
    }

    return Array.from(tags).slice(0, 30);
  }

  categorizeTags(tags: string[]): {
    primary: string[];
    secondary: string[];
    descriptive: string[];
  } {
    const categories = {
      primary: [] as string[],
      secondary: [] as string[],
      descriptive: [] as string[],
    };

    tags.forEach(tag => {
      const lowerTag = tag.toLowerCase();

      // Primary tags - brand and type
      if (
        lowerTag.includes('brand') ||
        lowerTag.includes('commercial') ||
        lowerTag.includes('fashion') ||
        lowerTag.includes('tech') ||
        lowerTag.includes('luxury') ||
        lowerTag.includes('sports')
      ) {
        categories.primary.push(tag);
      }
      // Secondary tags - campaign and placement
      else if (
        lowerTag.includes('campaign') ||
        lowerTag.includes('placement') ||
        lowerTag.includes('sponsor') ||
        lowerTag.includes('partnership')
      ) {
        categories.secondary.push(tag);
      }
      // Descriptive tags - specific details
      else {
        categories.descriptive.push(tag);
      }
    });

    return categories;
  }

  validateBusinessRules(metadata: BrandedLayerMetadata): ValidationResult {
    const errors: any[] = [];
    const warnings: any[] = [];

    // Business rule: Brand name should be meaningful
    if (metadata.brandName && metadata.brandName.trim().length < 2) {
      warnings.push({
        field: 'brandName',
        message: 'Brand name should be at least 2 characters long',
        suggestion: 'Provide a valid brand name',
      });
    }

    // Business rule: Should have clear brand integration
    if (!metadata.brandPlacement && !metadata.sponsorshipLevel) {
      warnings.push({
        field: 'brandPlacement',
        message:
          'Brand integration should specify placement or sponsorship level',
        suggestion: 'Define how the brand is integrated into the content',
      });
    }

    // Business rule: Commercial content should have proper disclosure
    if (
      metadata.sponsorshipLevel &&
      ['Title', 'Major'].includes(metadata.sponsorshipLevel) &&
      !metadata.disclosureRequired
    ) {
      warnings.push({
        field: 'disclosureRequired',
        message: 'Major sponsorships typically require disclosure',
        suggestion: 'Consider requiring disclosure for transparency',
      });
    }

    // Business rule: AI scores should be 0-1
    const aiFields = [
      'brandRecognition',
      'brandAlignment',
      'commercialAppeal',
      'authenticity',
      'viralPotential',
    ];
    aiFields.forEach(field => {
      if (
        metadata[field as keyof BrandedLayerMetadata] &&
        ((metadata[field as keyof BrandedLayerMetadata] as number) < 0 ||
          (metadata[field as keyof BrandedLayerMetadata] as number) > 1)
      ) {
        warnings.push({
          field,
          message: `${field} should be a value between 0 and 1`,
          suggestion: 'Provide a decimal value between 0 and 1',
        });
      }
    });

    // Business rule: Price should be positive
    if (metadata.productPrice && metadata.productPrice < 0) {
      warnings.push({
        field: 'productPrice',
        message: 'Product price should be a positive number',
        suggestion: 'Enter a valid price or remove if not applicable',
      });
    }

    // Business rule: Performance metrics should be reasonable
    if (
      metadata.conversionRate &&
      (metadata.conversionRate < 0 || metadata.conversionRate > 100)
    ) {
      warnings.push({
        field: 'conversionRate',
        message: 'Conversion rate should be between 0% and 100%',
        suggestion: 'Provide a realistic conversion rate percentage',
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  getDefaultMetadata(): BrandedLayerMetadata {
    return {
      layerType: 'branded',
      brandName: '',
      brandType: 'Fashion',
      description: '',
      brandColors: [],
      targetAudience: [],
      marketingObjectives: [],
      contractTerms: [],
      usageRights: [],
      regulatoryCompliance: [],
      geographicRestrictions: [],
      hashtags: [],
      socialPlatforms: [],
      trendAlignment: [],
      collaboratingBrands: [],
      productDetection: [],
      visualBrandElements: [],
      brandPlacement: 'Integrated',
      sponsorshipLevel: 'Supporting',
      influencerTier: 'Micro',
      partnershipType: 'Paid',
      currency: 'USD',
      exclusivity: false,
      disclosureRequired: true,
      integratedAssets: {},
    };
  }

  getInheritanceRules(): {
    [field: string]: 'always' | 'if_empty' | 'never' | 'merge';
  } {
    return {
      // Brand identity is unique
      brandName: 'never',
      brandType: 'never',
      brandLogo: 'never',

      // Could inherit campaign characteristics
      targetAudience: 'merge',
      marketingObjectives: 'merge',
      brandColors: 'merge',
      socialPlatforms: 'merge',
    };
  }

  private normalizeArray(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      return value
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    }
    return [];
  }

  private normalizeBrandType(value: any): BrandedLayerMetadata['brandType'] {
    if (!value) return 'Fashion';

    const normalized = String(value).toLowerCase().replace(/[\s-]/g, '_');

    if (normalized.includes('tech')) return 'Tech';
    if (normalized.includes('food') || normalized.includes('beverage'))
      return 'Food_Beverage';
    if (normalized.includes('auto') || normalized.includes('car'))
      return 'Automotive';
    if (normalized.includes('cosmetic') || normalized.includes('beauty'))
      return 'Cosmetics';
    if (normalized.includes('sport') || normalized.includes('athletic'))
      return 'Sports';
    if (normalized.includes('luxury') || normalized.includes('premium'))
      return 'Luxury';
    if (normalized.includes('entertainment') || normalized.includes('media'))
      return 'Entertainment';
    if (
      normalized.includes('crypto') ||
      normalized.includes('web3') ||
      normalized.includes('blockchain')
    )
      return 'Crypto_Web3';

    return 'Fashion';
  }

  private normalizeCampaignType(
    value: any
  ): BrandedLayerMetadata['campaignType'] {
    if (!value) return undefined;

    const normalized = String(value).toLowerCase().replace(/[\s-]/g, '_');

    if (normalized.includes('product') && normalized.includes('launch'))
      return 'Product_Launch';
    if (normalized.includes('brand') && normalized.includes('awareness'))
      return 'Brand_Awareness';
    if (normalized.includes('influencer') || normalized.includes('partnership'))
      return 'Influencer_Partnership';
    if (normalized.includes('seasonal') || normalized.includes('holiday'))
      return 'Seasonal';
    if (normalized.includes('event') || normalized.includes('promotion'))
      return 'Event_Promotion';

    return 'Brand_Awareness';
  }

  private normalizeContentType(
    value: any
  ): BrandedLayerMetadata['contentType'] {
    if (!value) return undefined;

    const normalized = String(value).toLowerCase();

    if (normalized.includes('video')) return 'Video';
    if (normalized.includes('mixed') || normalized.includes('multi'))
      return 'Mixed_Media';

    return 'Image';
  }

  private normalizeBrandPlacement(
    value: any
  ): BrandedLayerMetadata['brandPlacement'] {
    if (!value) return 'Integrated';

    const normalized = String(value).toLowerCase();

    if (normalized.includes('prominent') || normalized.includes('featured'))
      return 'Prominent';
    if (normalized.includes('subtle') || normalized.includes('organic'))
      return 'Subtle';
    if (normalized.includes('background') || normalized.includes('ambient'))
      return 'Background';

    return 'Integrated';
  }

  private normalizeSponsorshipLevel(
    value: any
  ): BrandedLayerMetadata['sponsorshipLevel'] {
    if (!value) return 'Supporting';

    const normalized = String(value).toLowerCase();

    if (normalized.includes('title')) return 'Title';
    if (normalized.includes('major') || normalized.includes('primary'))
      return 'Major';
    if (normalized.includes('mention') || normalized.includes('minor'))
      return 'Mention';

    return 'Supporting';
  }

  private normalizeInfluencerTier(
    value: any
  ): BrandedLayerMetadata['influencerTier'] {
    if (!value) return 'Micro';

    const normalized = String(value).toLowerCase();

    if (normalized.includes('nano')) return 'Nano';
    if (normalized.includes('macro')) return 'Macro';
    if (normalized.includes('mega')) return 'Mega';
    if (normalized.includes('celebrity')) return 'Celebrity';

    return 'Micro';
  }

  private normalizePartnershipType(
    value: any
  ): BrandedLayerMetadata['partnershipType'] {
    if (!value) return 'Paid';

    const normalized = String(value).toLowerCase().replace(/[\s-]/g, '_');

    if (normalized.includes('gifted') || normalized.includes('free'))
      return 'Gifted';
    if (normalized.includes('equity') || normalized.includes('share'))
      return 'Equity';
    if (normalized.includes('long') || normalized.includes('contract'))
      return 'Long_Term_Contract';

    return 'Paid';
  }

  private extractIntegratedAssets(
    assets: any
  ): BrandedLayerMetadata['integratedAssets'] {
    if (!assets) return {};

    return {
      songAssets: this.normalizeArray(
        assets.songAssets || assets.songs || assets.G
      ),
      starAssets: this.normalizeArray(
        assets.starAssets || assets.stars || assets.S
      ),
      lookAssets: this.normalizeArray(
        assets.lookAssets || assets.looks || assets.L
      ),
      moveAssets: this.normalizeArray(
        assets.moveAssets || assets.moves || assets.M
      ),
      worldAssets: this.normalizeArray(
        assets.worldAssets || assets.worlds || assets.W
      ),
      compositeAssets: this.normalizeArray(
        assets.compositeAssets || assets.composites || assets.C
      ),
    };
  }
}

// Export singleton instance
export const brandedLayerStrategy = new BrandedLayerStrategy();
